import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Stethoscope, ArrowLeft, User, Save } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ProfilePage() {
  const { doctorId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    specialty: '',
    city: '',
    country: '',
    target_audience: '',
    language_preference: 'English'
  });

  const specialties = [
    'Cardiology', 'Dermatology', 'Gynecology', 'Orthopedics', 'Pediatrics',
    'General Physician', 'ENT', 'Ophthalmology', 'Psychiatry', 'Dentistry'
  ];

  const countries = ['India', 'UAE', 'USA'];
  const languages = ['English', 'Hinglish', 'Hindi', 'Arabic'];

  useEffect(() => {
    fetchProfile();
  }, [doctorId]);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/doctors/${doctorId}`);
      setFormData({
        name: response.data.name || '',
        specialty: response.data.specialty || '',
        city: response.data.city || '',
        country: response.data.country || '',
        target_audience: response.data.target_audience || '',
        language_preference: response.data.language_preference || 'English'
      });
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!formData.name || !formData.specialty || !formData.city || !formData.country) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API}/doctors/${doctorId}`, formData);
      toast.success('Profile updated successfully!');
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#2C5F44] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[#5C7063]">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7] grain-texture">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center">
          <Button
            onClick={() => navigate(`/dashboard/${doctorId}`)}
            variant="ghost"
            size="sm"
            className="rounded-full mr-4"
            data-testid="profile-back-btn"
          >
            <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
          </Button>
          <Stethoscope className="w-8 h-8 text-[#2C5F44] mr-2" strokeWidth={1.5} />
          <span className="text-2xl font-bold text-[#2C5F44]" style={{ fontFamily: 'Nunito, sans-serif' }}>
            Profile Settings
          </span>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-3xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-3xl p-8 border border-[#2C5F44]/10"
        >
          <div className="flex items-center gap-4 mb-8">
            <div className="w-16 h-16 bg-[#82A08B]/20 rounded-full flex items-center justify-center">
              <User className="w-8 h-8 text-[#2C5F44]" strokeWidth={1.5} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[#1C2A21]" style={{ fontFamily: 'Nunito, sans-serif' }}>
                Doctor Profile
              </h1>
              <p className="text-[#5C7063]">Manage your profile information</p>
            </div>
          </div>

          <div className="space-y-6">
            <div>
              <Label htmlFor="name" className="text-[#1C2A21] font-medium mb-2 block">
                Full Name *
              </Label>
              <Input
                id="name"
                data-testid="profile-name-input"
                placeholder="Dr. John Smith"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className="rounded-xl border-[#2C5F44]/20"
              />
            </div>

            <div>
              <Label htmlFor="specialty" className="text-[#1C2A21] font-medium mb-2 block">
                Specialty *
              </Label>
              <Select value={formData.specialty} onValueChange={(val) => handleInputChange('specialty', val)}>
                <SelectTrigger 
                  data-testid="profile-specialty-select"
                  className="rounded-xl border-[#2C5F44]/20"
                >
                  <SelectValue placeholder="Select your specialty" />
                </SelectTrigger>
                <SelectContent>
                  {specialties.map((spec) => (
                    <SelectItem key={spec} value={spec}>{spec}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="city" className="text-[#1C2A21] font-medium mb-2 block">
                  City *
                </Label>
                <Input
                  id="city"
                  data-testid="profile-city-input"
                  placeholder="Mumbai"
                  value={formData.city}
                  onChange={(e) => handleInputChange('city', e.target.value)}
                  className="rounded-xl border-[#2C5F44]/20"
                />
              </div>

              <div>
                <Label htmlFor="country" className="text-[#1C2A21] font-medium mb-2 block">
                  Country *
                </Label>
                <Select value={formData.country} onValueChange={(val) => handleInputChange('country', val)}>
                  <SelectTrigger 
                    data-testid="profile-country-select"
                    className="rounded-xl border-[#2C5F44]/20"
                  >
                    <SelectValue placeholder="Select country" />
                  </SelectTrigger>
                  <SelectContent>
                    {countries.map((country) => (
                      <SelectItem key={country} value={country}>{country}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="target_audience" className="text-[#1C2A21] font-medium mb-2 block">
                Target Audience
              </Label>
              <Input
                id="target_audience"
                data-testid="profile-audience-input"
                placeholder="Young adults, Parents, Seniors, etc."
                value={formData.target_audience}
                onChange={(e) => handleInputChange('target_audience', e.target.value)}
                className="rounded-xl border-[#2C5F44]/20"
              />
            </div>

            <div>
              <Label htmlFor="language" className="text-[#1C2A21] font-medium mb-2 block">
                Language Preference
              </Label>
              <Select value={formData.language_preference} onValueChange={(val) => handleInputChange('language_preference', val)}>
                <SelectTrigger 
                  data-testid="profile-language-select"
                  className="rounded-xl border-[#2C5F44]/20"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {languages.map((lang) => (
                    <SelectItem key={lang} value={lang}>{lang}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button
              onClick={handleSave}
              disabled={saving}
              className="button-pill bg-[#2C5F44] hover:bg-[#244b36] text-white rounded-full w-full py-6 mt-8"
              data-testid="profile-save-btn"
            >
              {saving ? (
                'Saving...'
              ) : (
                <>
                  <Save className="w-5 h-5 mr-2" strokeWidth={1.5} />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
